import {
  NOTIFICATION_RATE_LIMIT_DEFAULT,
  NOTIFICATION_RATE_LIMIT_MAX,
  NotificationChannel,
  NotificationRuleKind,
  RECIPIENTS_MAX,
  SEVERITY_ORDER,
  Severity,
  SmtpSecurity,
  type EmailChannelView,
  type TelegramChannelView,
  type NotificationRule,
  type NotificationRuleInput,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { needsReauth } from '../api/client.js';
import {
  createNotificationRule,
  deleteNotificationRule,
  deleteTelegramChannel,
  getLatestSummary,
  getNotificationChannels,
  getNotificationSettings,
  previewSummary,
  saveNotificationSettings,
  saveTelegramChannel,
  testTelegramChannel,
  listNotificationMessages,
  listNotificationRules,
  saveEmailChannel,
  testEmailChannel,
  testNotificationRule,
  updateNotificationRule,
} from '../api/notifications.js';
import { listServerGroups, listServers } from '../api/servers.js';
import { useReauth } from '../auth/reauth-context.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * Notifications (knowledge doc sections 15 and 20; P9.1): the email server, the
 * rules that say who hears about which events, and what was sent.
 *
 * The password is write-only, as the API keeps it: the form shows whether one is
 * stored and offers to replace or remove it, never its value. Saving the server
 * asks for the re-check, because it is a credential.
 */

const errorText = (caught: unknown, fallback: string): string =>
  needsReauth(caught)
    ? 'That needed a fresh confirmation. Try again.'
    : caught instanceof Error
      ? caught.message
      : fallback;

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'Email',
  slack: 'Slack',
  telegram: 'Telegram',
};

const RECIPIENT_HINTS: Record<NotificationChannel, string> = {
  email: `Up to ${String(RECIPIENTS_MAX.email)} addresses, one per line or separated by commas.`,
  slack: `Up to ${String(RECIPIENTS_MAX.slack)} incoming webhooks (https://hooks.slack.com/services/…). Kept secret: shown afterwards only masked, and a masked one left as it is stays.`,
  telegram: `Up to ${String(RECIPIENTS_MAX.telegram)} chats: a chat id (a number) or a public channel's @name. The bot must be in the chat.`,
};

const SECURITY_LABELS: Record<SmtpSecurity, string> = {
  tls: 'TLS from the start (usually port 465)',
  starttls: 'STARTTLS, required (usually port 587)',
  none: 'None — only for a mail relay on the central server itself',
};

export function NotificationsPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const channels = useQuery({
    queryKey: ['notification-channels'],
    queryFn: getNotificationChannels,
  });
  const rules = useQuery({ queryKey: ['notification-rules'], queryFn: listNotificationRules });
  const messages = useQuery({
    queryKey: ['notification-messages'],
    queryFn: listNotificationMessages,
  });
  const [editing, setEditing] = useState<NotificationRule | 'new' | null>(null);

  return (
    <div className="page">
      <PageHeader
        title="Notifications"
        description="Email about events as they arrive. Each rule says who hears about what, and how often at most."
      />

      {channels.data !== undefined && <EmailServerCard current={channels.data.email} />}
      {channels.data !== undefined && <TelegramCard current={channels.data.telegram} />}
      <DailySummaryCard />

      <Card
        title="Rules"
        aside={
          editing === null ? (
            <Button
              variant="primary"
              onClick={() => {
                setEditing('new');
              }}
            >
              New rule
            </Button>
          ) : undefined
        }
      >
        {channels.data?.email === null && (
          <Alert tone="info">
            Rules can be made now, but nothing is sent until the email server is set up.
          </Alert>
        )}
        {editing !== null && (
          <RuleEditor
            rule={editing === 'new' ? null : editing}
            onDone={() => {
              setEditing(null);
            }}
          />
        )}
        {rules.data?.length === 0 && editing === null && (
          <EmptyState title="No rules yet">
            Make one to be told about events at or above a severity, on some servers or all.
          </EmptyState>
        )}
        {rules.data !== undefined && rules.data.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <caption className="visually-hidden">Notification rules</caption>
              <thead>
                <tr>
                  <th scope="col">Rule</th>
                  <th scope="col">Sends to</th>
                  <th scope="col">About</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.data.map((rule) => (
                  <RuleRow
                    key={rule.id}
                    rule={rule}
                    onEdit={() => {
                      setEditing(rule);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Recently sent" description="The last fifty messages, and why any did not go.">
        {messages.data?.length === 0 && (
          <EmptyState title="Nothing sent yet">Messages appear here as they go out.</EmptyState>
        )}
        {messages.data !== undefined && messages.data.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <caption className="visually-hidden">Messages sent, newest first</caption>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Rule</th>
                  <th scope="col">What</th>
                  <th scope="col">Result</th>
                </tr>
              </thead>
              <tbody>
                {messages.data.map((message) => (
                  <tr key={message.id}>
                    <td className="text-sm" title={formatInstant(message.createdAt, { timezone })}>
                      {formatRelative(message.createdAt)}
                    </td>
                    <td className="text-sm">{message.ruleName}</td>
                    <td className="text-sm">
                      {message.kind === 'test'
                        ? 'Test message'
                        : `${String(message.eventCount)} ${message.eventCount === 1 ? 'event' : 'events'}`}
                    </td>
                    <td className="text-sm">
                      {message.status === 'sent' ? (
                        <span className="badge badge-success">sent</span>
                      ) : (
                        <>
                          <span className="badge badge-danger">failed</span>
                          {message.error !== null && (
                            <span className="cell-sub">{message.error}</span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function EmailServerCard({ current }: { current: EmailChannelView | null }): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const [host, setHost] = useState(current?.host ?? '');
  const [port, setPort] = useState(String(current?.port ?? 587));
  const [security, setSecurity] = useState<SmtpSecurity>(current?.security ?? 'starttls');
  const [username, setUsername] = useState(current?.username ?? '');
  const [password, setPassword] = useState('');
  const [removePassword, setRemovePassword] = useState(false);
  const [from, setFrom] = useState(current?.from ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const save = useMutation({
    mutationFn: () =>
      saveEmailChannel({
        host: host.trim(),
        port: Number(port),
        security,
        username: username.trim() === '' ? null : username.trim(),
        from: from.trim(),
        ...(removePassword ? { password: null } : password === '' ? {} : { password }),
      }),
    onSuccess: () => {
      setPassword('');
      setRemovePassword(false);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
    },
    onError: (caught: unknown) => {
      setError(errorText(caught, 'Could not save the email settings.'));
    },
  });

  const test = useMutation({
    mutationFn: () => testEmailChannel(testTo.trim()),
    onSuccess: () => {
      setTestResult({ ok: true, text: `Sent to ${testTo.trim()}. Check that it arrived.` });
    },
    onError: (caught: unknown) => {
      setTestResult({ ok: false, text: errorText(caught, 'The test did not go.') });
    },
  });

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSaved(false);
    if (await ensureReauthenticated()) save.mutate();
  };

  return (
    <Card
      title="Email server"
      description="The mail server Bartizo sends through. The password is stored encrypted and never shown again."
    >
      <form onSubmit={(event) => void submit(event)} className="stack">
        <div className="filters">
          <TextField
            label="Server"
            placeholder="smtp.example.com"
            required
            value={host}
            onChange={(event) => {
              setHost(event.target.value);
            }}
          />
          <TextField
            label="Port"
            type="number"
            min={1}
            max={65535}
            required
            value={port}
            onChange={(event) => {
              setPort(event.target.value);
            }}
          />
          <SelectField
            label="Encryption"
            value={security}
            onChange={(event) => {
              const parsed = SmtpSecurity.safeParse(event.target.value);
              if (parsed.success) setSecurity(parsed.data);
            }}
          >
            {SmtpSecurity.options.map((option) => (
              <option key={option} value={option}>
                {SECURITY_LABELS[option]}
              </option>
            ))}
          </SelectField>
        </div>
        <div className="filters">
          <TextField
            label="User name"
            hint="Leave empty for a server that takes mail without signing in."
            autoComplete="off"
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
            }}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            hint={
              current?.hasPassword === true
                ? 'One is stored. Type a new one to replace it.'
                : undefined
            }
            disabled={removePassword}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          <TextField
            label="Send as"
            type="email"
            placeholder="bartizo@example.com"
            required
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
            }}
          />
        </div>
        {current?.hasPassword === true && (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={removePassword}
              onChange={(event) => {
                setRemovePassword(event.target.checked);
              }}
            />
            Remove the stored password
          </label>
        )}
        {error !== null && <Alert tone="danger">{error}</Alert>}
        {saved && <Alert tone="success">Saved.</Alert>}
        <div className="row">
          <Button type="submit" variant="primary" busy={save.isPending} busyLabel="Saving…">
            Save email server
          </Button>
        </div>
      </form>

      {current !== null && (
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
            setTestResult(null);
            test.mutate();
          }}
        >
          <TextField
            label="Send a test to"
            type="email"
            required
            value={testTo}
            onChange={(event) => {
              setTestTo(event.target.value);
            }}
          />
          <Button type="submit" busy={test.isPending} busyLabel="Sending…">
            Send test
          </Button>
        </form>
      )}
      {testResult !== null && (
        <Alert tone={testResult.ok ? 'success' : 'danger'}>{testResult.text}</Alert>
      )}
    </Card>
  );
}

const describeFilters = (rule: NotificationRule): string => {
  if (rule.kind === 'daily_summary') return 'The daily summary';
  const parts = [`${rule.minSeverity} and above`];
  if (rule.filters.serverIds !== undefined && rule.filters.serverIds.length > 0) {
    parts.push(`${String(rule.filters.serverIds.length)} servers`);
  }
  if (rule.filters.groupIds !== undefined && rule.filters.groupIds.length > 0) {
    parts.push(`${String(rule.filters.groupIds.length)} groups`);
  }
  if (rule.filters.categories !== undefined && rule.filters.categories.length > 0) {
    parts.push(rule.filters.categories.join(', '));
  }
  return parts.join(' · ');
};

function RuleRow({ rule, onEdit }: { rule: NotificationRule; onEdit: () => void }): ReactNode {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const remove = useMutation({
    mutationFn: () => deleteNotificationRule(rule.id),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['notification-rules'] });
    },
  });
  const test = useMutation({
    mutationFn: () => testNotificationRule(rule.id),
    onSuccess: () => {
      setNote({ ok: true, text: 'Test sent.' });
      void queryClient.invalidateQueries({ queryKey: ['notification-messages'] });
    },
    onError: (caught: unknown) => {
      setNote({ ok: false, text: errorText(caught, 'The test did not go.') });
      void queryClient.invalidateQueries({ queryKey: ['notification-messages'] });
    },
  });

  return (
    <tr className={rule.enabled ? undefined : 'row-done'}>
      <td>
        {rule.name}
        <span className="cell-sub">
          {CHANNEL_LABELS[rule.channel]} · {rule.enabled ? 'on' : 'off'}
          {rule.kind === 'events' && ` · at most ${String(rule.rateLimit)} a hour`}
          {rule.pending > 0 && ` · ${String(rule.pending)} waiting`}
        </span>
      </td>
      <td className="text-sm">{rule.recipients.join(', ')}</td>
      <td className="text-sm">{describeFilters(rule)}</td>
      <td>
        <div className="row">
          <Button variant="ghost" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="ghost"
            busy={test.isPending}
            onClick={() => {
              setNote(null);
              test.mutate();
            }}
          >
            Test
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Delete
          </Button>
        </div>
        {note !== null && (
          <span
            className={note.ok ? 'cell-sub' : 'field-error'}
            role={note.ok ? undefined : 'alert'}
          >
            {note.text}
          </span>
        )}
        {confirming && (
          <ConfirmDialog
            title={`Delete “${rule.name}”?`}
            description="Nobody is told about its events any more. What it already sent stays in the list below until it ages out."
            confirmLabel="Delete the rule"
            danger
            busy={remove.isPending}
            onConfirm={() => {
              remove.mutate();
            }}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        )}
      </td>
    </tr>
  );
}

const splitList = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((each) => each.trim())
    .filter((each) => each.length > 0);

function RuleEditor({
  rule,
  onDone,
}: {
  rule: NotificationRule | null;
  onDone: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const servers = useQuery({ queryKey: ['servers'], queryFn: () => listServers({ limit: 200 }) });
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });

  const [name, setName] = useState(rule?.name ?? '');
  const [kind, setKind] = useState<NotificationRuleKind>(rule?.kind ?? 'events');
  const [channel, setChannel] = useState<NotificationChannel>(rule?.channel ?? 'email');
  const [recipients, setRecipients] = useState(rule?.recipients.join('\n') ?? '');
  const [minSeverity, setMinSeverity] = useState<Severity>(rule?.minSeverity ?? 'high');
  const [serverIds, setServerIds] = useState<string[]>(rule?.filters.serverIds ?? []);
  const [groupIds, setGroupIds] = useState<string[]>(rule?.filters.groupIds ?? []);
  const [categories, setCategories] = useState(rule?.filters.categories?.join(', ') ?? '');
  const [rateLimit, setRateLimit] = useState(
    String(rule?.rateLimit ?? NOTIFICATION_RATE_LIMIT_DEFAULT),
  );
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const cats = splitList(categories);
      const body: NotificationRuleInput = {
        name: name.trim(),
        kind,
        channel,
        recipients: splitList(recipients),
        minSeverity,
        // The summary is the fleet's: a summary rule takes no filters.
        filters:
          kind === 'daily_summary'
            ? {}
            : {
                ...(serverIds.length > 0 ? { serverIds } : {}),
                ...(groupIds.length > 0 ? { groupIds } : {}),
                ...(cats.length > 0 ? { categories: cats } : {}),
              },
        rateLimit: Number(rateLimit),
        enabled,
      };
      return rule === null ? createNotificationRule(body) : updateNotificationRule(rule.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notification-rules'] });
      onDone();
    },
    onError: (caught: unknown) => {
      setError(errorText(caught, 'Could not save the rule.'));
    },
  });

  const toggle = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter((each) => each !== id) : [...list, id];

  return (
    <form
      className="stack fieldset"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <h3>{rule === null ? 'New rule' : `Edit “${rule.name}”`}</h3>
      <div className="filters">
        <TextField
          label="Name"
          required
          maxLength={100}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <SelectField
          label="What it sends"
          value={kind}
          onChange={(event) => {
            const parsed = NotificationRuleKind.safeParse(event.target.value);
            if (parsed.success) setKind(parsed.data);
          }}
        >
          <option value="events">Events as they arrive</option>
          <option value="daily_summary">The daily summary</option>
        </SelectField>
        <SelectField
          label="Channel"
          value={channel}
          onChange={(event) => {
            const parsed = NotificationChannel.safeParse(event.target.value);
            if (parsed.success) setChannel(parsed.data);
          }}
        >
          {NotificationChannel.options.map((option) => (
            <option key={option} value={option}>
              {CHANNEL_LABELS[option]}
            </option>
          ))}
        </SelectField>
      </div>
      {kind === 'events' && (
        <div className="filters">
          <SelectField
            label="Severity"
            hint="This level and everything worse."
            value={minSeverity}
            onChange={(event) => {
              const parsed = Severity.safeParse(event.target.value);
              if (parsed.success) setMinSeverity(parsed.data);
            }}
          >
            {[...SEVERITY_ORDER].reverse().map((option) => (
              <option key={option} value={option}>
                {option} and above
              </option>
            ))}
          </SelectField>
          <TextField
            label="At most, messages an hour"
            hint="More events than that wait and go out together."
            type="number"
            min={1}
            max={NOTIFICATION_RATE_LIMIT_MAX}
            value={rateLimit}
            onChange={(event) => {
              setRateLimit(event.target.value);
            }}
          />
        </div>
      )}
      <div className="field">
        <label htmlFor="rule-recipients">Send to</label>
        <span className="field-hint" id="rule-recipients-hint">
          {RECIPIENT_HINTS[channel]}
        </span>
        <textarea
          id="rule-recipients"
          aria-describedby="rule-recipients-hint"
          rows={3}
          required
          value={recipients}
          onChange={(event) => {
            setRecipients(event.target.value);
          }}
        />
      </div>
      {kind === 'events' && (
        <>
          <TextField
            label="Categories"
            hint="Leave empty for every category; otherwise e.g. fim, bruteforce, disk."
            value={categories}
            onChange={(event) => {
              setCategories(event.target.value);
            }}
          />
          {(groups.data ?? []).length > 0 && (
            <fieldset className="fieldset stack">
              <legend>Only these groups (none ticked is every group)</legend>
              {(groups.data ?? []).map((group) => (
                <label key={group.id} className="checkbox">
                  <input
                    type="checkbox"
                    checked={groupIds.includes(group.id)}
                    onChange={() => {
                      setGroupIds((current) => toggle(current, group.id));
                    }}
                  />
                  {group.name}
                </label>
              ))}
            </fieldset>
          )}
          {(servers.data?.items ?? []).length > 0 && (
            <fieldset className="fieldset stack">
              <legend>Only these servers (none ticked is every server)</legend>
              {(servers.data?.items ?? []).map((server) => (
                <label key={server.id} className="checkbox">
                  <input
                    type="checkbox"
                    checked={serverIds.includes(server.id)}
                    onChange={() => {
                      setServerIds((current) => toggle(current, server.id));
                    }}
                  />
                  {server.name}
                </label>
              ))}
            </fieldset>
          )}
        </>
      )}
      <label className="checkbox">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => {
            setEnabled(event.target.checked);
          }}
        />
        On
      </label>
      {error !== null && <Alert tone="danger">{error}</Alert>}
      <div className="row">
        <Button type="submit" variant="primary" busy={save.isPending} busyLabel="Saving…">
          {rule === null ? 'Create rule' : 'Save rule'}
        </Button>
        <Button onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

/** The Telegram bot (P9.3): its token is a credential, written once and never shown. */
function TelegramCard({ current }: { current: TelegramChannelView | null }): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
  };

  const save = useMutation({
    mutationFn: () => saveTelegramChannel(token.trim()),
    onSuccess: (saved) => {
      setToken('');
      setNote({ ok: true, text: `Saved: the bot is @${saved.botUsername ?? '?'}.` });
      refresh();
    },
    onError: (caught: unknown) => {
      setNote({ ok: false, text: errorText(caught, 'Could not save the token.') });
    },
  });
  const remove = useMutation({
    mutationFn: deleteTelegramChannel,
    onSuccess: () => {
      setNote({ ok: true, text: 'Removed. Telegram rules wait until a bot is set up again.' });
      refresh();
    },
    onError: (caught: unknown) => {
      setNote({ ok: false, text: errorText(caught, 'Could not remove the token.') });
    },
  });
  const test = useMutation({
    mutationFn: () => testTelegramChannel(chatId.trim()),
    onSuccess: () => {
      setNote({ ok: true, text: `Sent to ${chatId.trim()}. Check that it arrived.` });
    },
    onError: (caught: unknown) => {
      setNote({ ok: false, text: errorText(caught, 'The test did not go.') });
    },
  });

  return (
    <Card
      title="Telegram"
      description={
        current === null
          ? 'A bot from @BotFather sends Telegram rules’ messages. Paste its token; Telegram is asked who the bot is before it is kept.'
          : `Messages go from @${current.botUsername ?? 'your bot'}. The token is stored encrypted and never shown again.`
      }
    >
      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          setNote(null);
          void ensureReauthenticated().then((ok) => {
            if (ok) save.mutate();
          });
        }}
      >
        <TextField
          label={current === null ? 'Bot token' : 'Replace the bot token'}
          type="password"
          autoComplete="off"
          required
          value={token}
          onChange={(event) => {
            setToken(event.target.value);
          }}
        />
        <Button type="submit" variant="primary" busy={save.isPending} busyLabel="Checking…">
          Save bot
        </Button>
        {current !== null && (
          <Button
            busy={remove.isPending}
            onClick={() => {
              setNote(null);
              void ensureReauthenticated().then((ok) => {
                if (ok) remove.mutate();
              });
            }}
          >
            Remove
          </Button>
        )}
      </form>
      {current !== null && (
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
            setNote(null);
            test.mutate();
          }}
        >
          <TextField
            label="Send a test to chat"
            placeholder="-1001234567890 or @channel"
            required
            value={chatId}
            onChange={(event) => {
              setChatId(event.target.value);
            }}
          />
          <Button type="submit" busy={test.isPending} busyLabel="Sending…">
            Send test
          </Button>
        </form>
      )}
      {note !== null && <Alert tone={note.ok ? 'success' : 'danger'}>{note.text}</Alert>}
    </Card>
  );
}

/**
 * The daily summary (P9.3): when it is made, the latest one and a preview. The
 * hour is kept in UTC (section 20) and shown here in the reader's own time too.
 */
function DailySummaryCard(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['notification-settings'],
    queryFn: getNotificationSettings,
  });
  const latest = useQuery({ queryKey: ['summary-latest'], queryFn: getLatestSummary });
  const [hour, setHour] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);
  const preview = useMutation({ mutationFn: previewSummary });
  const save = useMutation({
    mutationFn: (summaryHour: number) => saveNotificationSettings({ summaryHour }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
    },
  });
  const shown = hour ?? settings.data?.summaryHour ?? 7;
  const local = (utcHour: number): string => {
    const at = new Date(Date.UTC(2026, 0, 15, utcHour));
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    }).format(at);
  };
  const summary = latest.data ?? null;

  return (
    <Card
      title="Daily summary"
      description="Once a day, the figures for the last 24 hours and the worst events, with a model’s reading of them when AI is set up. Rules that send “the daily summary” receive it."
    >
      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          setSaved(false);
          save.mutate(shown);
        }}
      >
        <SelectField
          label="Made at (UTC)"
          hint={`That is ${local(shown)} for you.`}
          value={String(shown)}
          onChange={(event) => {
            setHour(Number(event.target.value));
          }}
        >
          {Array.from({ length: 24 }, (_, each) => (
            <option key={each} value={String(each)}>
              {`${String(each).padStart(2, '0')}:00 UTC`}
            </option>
          ))}
        </SelectField>
        <Button type="submit" variant="primary" busy={save.isPending} busyLabel="Saving…">
          Save time
        </Button>
        <Button
          busy={preview.isPending}
          onClick={() => {
            preview.mutate();
          }}
        >
          Preview the figures
        </Button>
      </form>
      {saved && <Alert tone="success">Saved.</Alert>}
      {summary !== null && (
        <div className="stack">
          <p>
            <strong>Latest: {summary.day}.</strong>{' '}
            {summary.narrative?.headline ??
              (summary.aiNote === null ? '' : `No model’s reading: ${summary.aiNote}.`)}
          </p>
          {summary.deliveries.length > 0 && (
            <p className="text-sm">
              {summary.deliveries.map((each) => `${each.ruleName}: ${each.status}`).join(' · ')}
            </p>
          )}
        </div>
      )}
      {preview.data !== undefined && <pre className="raw">{preview.data.text}</pre>}
    </Card>
  );
}
